import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useState, useEffect } from 'react';
import { useFormNavigation } from '@features/onboarding/components/shared/lib/contexts/FormNavigationContext';
import { ONBOARDING_STEP_KEY } from '@constants/index';

export const useFormControls = () => {
  const {
    currentStep,
    totalSteps,
    isSliderStep,
    activeSliderTab,
    totalSliderTabs,
    setCurrentStep,
    setTotalSteps,
    setSliderStep,
    setActiveSliderTab,
    setTotalSliderTabs,
    data: formData,
    setData,
    resetData,
    availableSteps,
    setAvailableSteps,
  } = useOnboardingStore();

  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const formSteps = useFormSteps();
  const currentFormStep = formSteps.at(currentStep);

  // Ensure currentStep is within bounds when formSteps change (e.g., medicalPlan removed)
  useEffect(() => {
    if (currentStep >= formSteps.length) {
      const newIndex = Math.max(formSteps.length - 1, 0);
      setCurrentStep(newIndex);
      localStorage.setItem(ONBOARDING_STEP_KEY, newIndex.toString());
    }
  }, [formSteps.length]);

  const setSlide = (newDirection: 1 | -1) => {
    if (isSliderStep) {
      const newTab = activeSliderTab + newDirection;
      if (newTab >= 0 && newTab < totalSliderTabs) {
        setActiveSliderTab(newTab);
        return;
      }
      if (newTab < 0) {
        setSliderStep(false);
        setActiveSliderTab(0);
        setTotalSliderTabs(0);
        const newStep = currentStep - 1;
        setCurrentStep(newStep);
        localStorage.setItem(ONBOARDING_STEP_KEY, newStep.toString());
        return;
      }
      if (newTab >= totalSliderTabs) {
        setSliderStep(false);
        setActiveSliderTab(0);
        setTotalSliderTabs(0);
      }
    }

    const newStep = currentStep + newDirection;
    if (newStep < 0 || newStep >= formSteps.length) return;

    setCurrentStep(newStep);

    localStorage.setItem(ONBOARDING_STEP_KEY, newStep.toString());
  };

  // Default handlers
  const defaultHandleNextStep = (data: Partial<OnboardingSchema>) => {
    setData(data);
    setSlide(1);
  };

  const defaultHandlePreviousStep = () => {
    setSlide(-1);
  };

  const defaultHandleSliderTabChange = (tab: number) => {
    if (isSliderStep) {
      setActiveSliderTab(tab);
    }
  };

  const handleJumpTo = (value: string) => {
    // Проверяем, является ли это переключением между категориями планов в Dental and Vision форме
    const dentalVisionCategories = ['Vision Plans', 'Accident Plans', 'Critical Illness Plans', 'Hospital Indemnity Plans'];

    if (dentalVisionCategories.includes(value)) {
      // Находим индекс Dental and Vision формы
      const dentalFormIndex = formSteps.findIndex((item) => item.sectionName === 'Dental and Vision');
      if (dentalFormIndex === -1) return;

      // Устанавливаем текущий шаг на Dental and Vision форму
      setCurrentStep(dentalFormIndex);
      setSliderStep(false);
      setActiveSliderTab(0);
      setTotalSliderTabs(0);

      // Сохраняем состояние шага в localStorage
      localStorage.setItem(ONBOARDING_STEP_KEY, dentalFormIndex.toString());

      // Обновляем категорию в zustand и эмитируем событие для совместимости
      try {
        // Чтобы избежать циклического импорта, используем require
        const { useOnboardingStore } = require('@features/onboarding/store/onboarding');
        useOnboardingStore.getState().setActiveDentalVisionCategory(value);
      } catch {
        /* no-op */
      }

      return;
    }

    // Обычная логика для других секций
    const index = formSteps.findIndex((item) => item.sectionName === value);
    if (index === -1) return;

    setCurrentStep(index);
    setSliderStep(false);
    setActiveSliderTab(0);
    setTotalSliderTabs(0);
    
    // Сохраняем состояние шага в localStorage
    localStorage.setItem(ONBOARDING_STEP_KEY, index.toString());
  };

  // Initialize navigation properties with defaults
  let handleNextStep = defaultHandleNextStep;
  let handlePreviousStep = defaultHandlePreviousStep;
  let handleSliderTabChange = defaultHandleSliderTabChange;

  // Try to get navigation handlers from context
  try {
    const navigationProps = useFormNavigation();
    handleNextStep = (data: Partial<OnboardingSchema>) => {
      setData(data);
      navigationProps.handleNext();
    };
    handlePreviousStep = navigationProps.handlePrevious;
    handleSliderTabChange = navigationProps.handleSliderTabChange;
  } catch {
    // If context is not provided, use default handlers
  }

  return {
    selectedStep: currentStep,
    formData,
    currentFormStep,
    formSteps,
    isSliderStep,
    activeSliderTab,
    totalSliderTabs,
    availableSteps,
    isLocalLoading,
    setIsLocalLoading,
    setData,
    resetData,
    setTotalSteps,
    setCurrentStep,
    defaultHandlePreviousStep,
    setSlide,
    handlePreviousStep,
    handleNextStep,
    handleJumpTo,
    handleSliderTabChange,
    setAvailableSteps,
    setSliderStep,
    setActiveSliderTab,
    setTotalSliderTabs,
  };
};
